<template>
  <section
    class="lead-flow-board ui-kanban"
    aria-label="Lead Flow board"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <LeadNewLeadsColumn
      v-if="showNewLeadsBacklog"
      ref="newLeads"
      :key="filters.search || 'unfiltered'"
      :load-leads="loadNewLeads"
      :search="filters.search || ''"
      @open="$emit('open', $event)"
      @assigned="handleNewLeadAssigned"
    />

    <template v-if="loading">
      <section v-for="index in skeletonColumnCount" :key="`loading-${index}`" class="ui-kanban-column" aria-hidden="true">
        <header class="ui-kanban-column__header">
          <span class="ui-kanban-column__loading-title" />
          <span class="ui-kanban-column__loading-description" />
        </header>
        <AppContentSkeleton layout="block" />
      </section>
    </template>

    <AppErrorState v-else-if="error" class="ui-kanban__state" message="Unable to load board columns. Try loading the active columns again." @retry="$emit('retry')" />

    <template v-else>
      <app-no-results v-if="!orderedColumns.length && !showNewLeadsBacklog" design="new" title="No leads found" text="Try changing or clearing the filters." />
      <LeadFlowColumn
        v-for="(column, columnIndex) in orderedColumns"
        :key="column.code"
        ref="columns"
        :column="column"
        :filters="filters"
        :generation="generation"
        :load-cards="loadCards"
        :last-stage="columnIndex === orderedColumns.length - 1"
        :now="now"
        @open="$emit('open', $event)"
      />
    </template>
  </section>
</template>

<script>
import AppErrorState from '@/components/AppErrorState.vue'
import AppContentSkeleton from '@/components/AppContentSkeleton.vue'
import LeadFlowColumn from '@/views/crm/task-tracker/LeadFlowColumn.vue'
import LeadNewLeadsColumn from '@/views/crm/task-tracker/LeadNewLeadsColumn.vue'

export default {
  name: 'LeadFlowBoard',
  components: {
    AppErrorState,
    AppContentSkeleton,
    LeadFlowColumn,
    LeadNewLeadsColumn
  },
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    filters: {
      type: Object,
      required: true
    },
    generation: {
      type: Number,
      required: true
    },
    loadCards: {
      type: Function,
      default: null
    },
    loadNewLeads: {
      type: Function,
      required: true
    },
    showNewLeadsBacklog: {
      type: Boolean,
      default: false
    },
    scope: {
      type: Object,
      required: true
    },
    currentUserId: {
      type: Number,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      default: null
    }
  },
  data() {
    return {
      skeletonColumnCount: 6,
      reconciledAssignments: {},
      reconciledMetadata: {},
      now: Date.now(),
      minuteTimer: null
    }
  },
  watch: {
    generation() {
      this.reconciledAssignments = {}
      this.reconciledMetadata = {}
    }
  },
  computed: {
    orderedColumns() {
      return this.columns
        .filter(column => Number(column.count) > 0)
        .sort((left, right) => left.sort - right.sort)
    }
  },
  mounted() {
    this.minuteTimer = setInterval(() => {
      this.now = Date.now()
    }, 60000)
  },
  beforeDestroy() {
    clearInterval(this.minuteTimer)
  },
  methods: {
    guidanceRows() {
      const newLeads = this.$refs.newLeads ? this.$refs.newLeads.leads : []
      const cards = (this.$refs.columns || []).reduce((all, column) => all.concat(column.cards), [])
      return newLeads.concat(cards)
    },
    refreshColumn(code) {
      const column = (this.$refs.columns || []).find(item => item.column.code === code)
      return column ? column.refresh() : Promise.resolve()
    },
    refreshColumns(codes) {
      const uniqueCodes = [...new Set((codes || []).filter(Boolean))]
      return Promise.all(uniqueCodes.map(code => this.refreshColumn(code)))
    },
    updateCard(card) {
      return (this.$refs.columns || []).some(column => column.updateCard(card))
    },
    handleNewLeadAssigned(assignment) {
      const leadId = Number(assignment.id)
      if (!Number.isInteger(leadId) || leadId <= 0) return

      const isInScope = this.assignmentIsInScope(assignment)
      const shouldRefreshMetadata = assignment.source === 'http' || isInScope
      if (shouldRefreshMetadata && !this.reconciledMetadata[leadId]) {
        this.$set(this.reconciledMetadata, leadId, true)
        this.$emit('metadata-refresh')
      }

      if (!isInScope || this.reconciledAssignments[leadId]) return
      this.$set(this.reconciledAssignments, leadId, true)
      this.refreshColumn(this.assignmentColumnCode(assignment))
    },
    assignmentIsInScope(assignment) {
      if (this.scope.mode === 'all') return true
      if (assignment.source === 'http' && this.scope.mode === 'own') return true

      const managerId = this.assignmentManagerId(assignment)
      if (!managerId) return false
      if (this.scope.mode === 'own') return managerId === this.currentUserId
      if (this.scope.mode !== 'manager') return false

      const selectedManager = this.scope.selected_manager
      return Boolean(selectedManager) && Number(selectedManager.id) === managerId
    },
    assignmentManagerId(assignment) {
      const delta = assignment.tracker_delta || {}
      const currentDelta = delta.current || {}
      const manager = assignment.manager || {}
      const managerId = Number(manager.id || currentDelta.manager_id)
      if (Number.isInteger(managerId) && managerId > 0) return managerId
      return assignment.source === 'http' ? this.currentUserId : null
    },
    assignmentColumnCode(assignment) {
      const delta = assignment.tracker_delta || {}
      const currentDelta = delta.current || {}
      if (currentDelta.column) return currentDelta.column

      const currentStage = assignment.current_stage || {}
      const matchingColumn = this.columns.find(column => {
        return (column.stage_codes || []).includes(currentStage.code)
      })
      return matchingColumn ? matchingColumn.code : 'first_contact'
    }
  }
}
</script>
